/**
 * Insert HTML into the patient report editor without stealing DOM focus
 * (avoids sidebar / Attached Documents UI flicker).
 */

function readEditorHtml(editor) {
  if (typeof editor.getHTML === "function") return editor.getHTML();
  if (typeof editor.getContents === "function") return editor.getContents();
  return "";
}

function syncTranscript(editor, setTranscript) {
  if (typeof setTranscript !== "function") return;
  const newContent = readEditorHtml(editor);
  if (newContent != null) setTranscript(newContent);
}

/**
 * Insert at the current document selection without calling editor.focus().
 */
function insertAtSelectionWithoutFocus(editor, html) {
  if (!editor?.chain || !editor?.state?.selection) {
    return false;
  }

  const { from, to } = editor.state.selection;
  const hasSelection = from !== to;

  let chain = editor.chain();
  if (hasSelection) {
    chain = chain.deleteRange({ from, to });
  }

  const insertPos = from;
  const inserted = chain
    .insertContentAt(insertPos, html, { updateSelection: false })
    .run();

  return inserted !== false;
}

/**
 * Append HTML at the end of the document without focusing the editor.
 */
function appendAtEndWithoutFocus(editor, html) {
  if (!editor?.chain || !editor?.state?.doc) {
    return false;
  }

  let docSize = 0;
  try {
    docSize = editor.state.doc.content.size;
  } catch {
    return false;
  }

  const inserted = editor
    .chain()
    .insertContentAt(docSize, html, { updateSelection: false })
    .run();

  return inserted !== false;
}

function appendViaSetContents(editor, html) {
  const currentContent = readEditorHtml(editor);
  const separator = currentContent && currentContent.trim() ? "\n\n" : "";
  const newContent = currentContent + separator + html;

  if (typeof editor.setContents === "function") {
    editor.setContents(newContent);
    return true;
  }

  return false;
}

/**
 * @param {object} options
 * @param {object} options.editor - RTE wrapper from editorRef.current.editor
 * @param {string} options.html - HTML to insert
 * @param {Function} [options.setTranscript] - Patient transcript setter
 * @param {Function} [options.onError] - Called if all strategies fail
 * @returns {{ ok: boolean, reason?: string }}
 */
export function insertHtmlIntoReportEditor({
  editor,
  html,
  setTranscript,
  onError,
}) {
  const contentStr = typeof html === "string" ? html : String(html ?? "");
  if (!contentStr || !contentStr.trim()) {
    return { ok: false, reason: "empty" };
  }
  if (!editor) {
    return { ok: false, reason: "no-editor" };
  }

  queueMicrotask(() => {
    try {
      if (insertAtSelectionWithoutFocus(editor, contentStr)) {
        syncTranscript(editor, setTranscript);
        return;
      }

      if (appendAtEndWithoutFocus(editor, contentStr)) {
        syncTranscript(editor, setTranscript);
        return;
      }

      if (appendViaSetContents(editor, contentStr)) {
        syncTranscript(editor, setTranscript);
        return;
      }

      onError?.(new Error("No insert strategy succeeded"));
    } catch (err) {
      try {
        if (appendViaSetContents(editor, contentStr)) {
          syncTranscript(editor, setTranscript);
          return;
        }
      } catch (fallbackErr) {
        onError?.(fallbackErr);
        return;
      }
      onError?.(err);
    }
  });

  return { ok: true };
}
